import { NPS_COLORS } from "@/lib/chartPalettes";
import { TrendingUp, Table2, SeparatorHorizontal, Type, Gauge, LineChart, Image as ImageIcon, Filter, Network, Blend, Map as MapIcon, Cloud, Target, Boxes } from "lucide-vue-next";

export const CONTENT_CARD_TYPES = [
  { key: "section", name: "Section title", label: "Section", description: "A titled divider", icon: SeparatorHorizontal },
  { key: "text", name: "Text block", label: "Text block", description: "Rich text between charts", icon: Type },
  { key: "image", name: "Image", label: "Image", description: "An image with a caption", icon: ImageIcon },
];

export const ANALYSIS_TYPES = [
  {
    key: "importance_performance",
    name: "IPA",
    label: "Importance-Performance",
    description: "Priorities from a satisfaction matrix",
    icon: TrendingUp,
  },
  {
    key: "key_drivers",
    name: "Key drivers",
    label: "Key drivers",
    description: "Which aspects move a global score",
    icon: Target,
  },
  {
    key: "turf",
    name: "TURF",
    label: "TURF",
    description: "The answer combination with the widest reach",
    icon: Boxes,
  },
  {
    key: "crosstab",
    name: "Crosstab",
    label: "Crosstab",
    description: "A table crossing questions",
    icon: Table2,
  },
  {
    key: "kpi",
    name: "KPI",
    label: "KPI",
    description: "A single number",
    icon: Gauge,
  },
  {
    key: "kpi_trend",
    name: "KPI trend",
    label: "KPI trend",
    description: "A number across waves",
    icon: LineChart,
  },
  {
    key: "funnel",
    name: "Funnel",
    label: "Funnel",
    description: "Respondent-level conversion across stages",
    icon: Filter,
  },
  {
    key: "sankey",
    name: "Flow diagram",
    label: "Flow diagram",
    description: "Respondents flow between two questions (switching, before/after analysis)",
    icon: Network,
  },
  {
    key: "overlap",
    name: "Overlap",
    label: "Overlap",
    description: "Who picked answer A and answer B",
    icon: Blend,
  },
  {
    key: "geo_map",
    name: "Map",
    label: "Map",
    description: "Respondents by area from a zipcode question",
    icon: MapIcon,
  },
  {
    key: "word_cloud",
    name: "Word cloud",
    label: "Word cloud",
    description: "Most used words of an open question",
    icon: Cloud,
  },
];

// Mirrors SurveyResults::Turf::MAX_COMBO_SIZE.
export const TURF_COMBO_SIZES = [2, 3, 4];
export const TURF_DEFAULT_COMBO_SIZE = 3;

export const GEO_COUNTRIES = [
  {
    value: "FR",
    label: "France",
    targetCountries: ["FR", "GP", "MQ", "GF", "RE", "YT", "PM", "BL", "MF", "WF", "PF", "NC"],
  },
];

export const surveyHasGeoCountry = (targets = []) =>
  targets.some((target) =>
    (target.countries || []).some((code) => GEO_COUNTRIES.some((country) => country.targetCountries.includes(code))),
  );

const CARD_TYPE_META = Object.fromEntries([...CONTENT_CARD_TYPES, ...ANALYSIS_TYPES].map((t) => [t.key, t]));

// Generic variant discriminator: the family is card_type, the variant is content.sub_type
// (e.g. an analysis card's "crosstab" / "importance_performance"). Null when the card has none.
export function cardSubtype(card) {
  return card?.content?.sub_type ?? null;
}

// Content-family sub_type (section / text / image); null for non-content cards.
export function contentSubtype(card) {
  return card?.card_type === "content" ? cardSubtype(card) : null;
}

const GRID_HALF_TYPES = new Set(["question", "recode", "analysis"]);
const GRID_FULL_SUBTYPES = new Set(["crosstab"]);

export function isHalfWidthCard(card, layout) {
  if (contentSubtype(card) === "section") return false;
  const explicit = card?.options?.width;
  if (explicit) return explicit === "half";
  if (GRID_FULL_SUBTYPES.has(cardSubtype(card))) return false;
  return layout === "grid" && GRID_HALF_TYPES.has(card?.card_type);
}

export const IMAGE_SIZE_MIN = 10;
export const IMAGE_SIZE_MAX = 100;

export function imageSizePercent(card) {
  const percent = Number(card?.options?.size_percent);
  if (!Number.isFinite(percent)) return IMAGE_SIZE_MAX;
  return Math.min(IMAGE_SIZE_MAX, Math.max(IMAGE_SIZE_MIN, Math.round(percent)));
}

export function cardTypeMeta(key) {
  return CARD_TYPE_META[key] || null;
}

export const CHART_TYPES = {
  BAR_CLUSTERED: "bar_clustered",
  BAR_STACKED_100: "bar_stacked_100",
  COLUMN_CLUSTERED: "column_clustered",
  COLUMN_STACKED_100: "column_stacked_100",
  PIE: "pie",
  PIE_EXPLODED: "pie_exploded",
  DOUGHNUT: "doughnut",
  NPS: "nps",
  HEATMAP: "heatmap",
  RADAR: "radar",
  PODIUM: "podium",
  DOT_PLOT: "dot_plot",
  RANKING_FLOW: "ranking_flow",
};

export const SINGLE_SERIES_CHART_TYPES = [
  CHART_TYPES.PIE,
  CHART_TYPES.PIE_EXPLODED,
  CHART_TYPES.DOUGHNUT,
  CHART_TYPES.HEATMAP,
  CHART_TYPES.PODIUM,
];

export const CHART_TYPE_OPTIONS = [
  { value: "bar_clustered", label: "Horizontal bar" },
  { value: "column_clustered", label: "Vertical bar" },
  { value: "bar_stacked_100", label: "Horizontal stacked 100%" },
  { value: "column_stacked_100", label: "Vertical stacked 100%" },
  { value: "nps", label: "NPS" },
  { value: "pie", label: "Pie" },
  { value: "doughnut", label: "Doughnut" },
  { value: "heatmap", label: "Heatmap (matrix)" },
  { value: "radar", label: "Radar (matrix single)" },
  { value: "podium", label: "Podium (top 3)" },
  { value: "dot_plot", label: "Dot plot (populations)" },
];

export const NPS_BUCKETS = [
  { code: "detractors", label: "Detractors", color: NPS_COLORS.detractors, codes: ["0", "1", "2", "3", "4", "5", "6"] },
  { code: "passives", label: "Passives", color: NPS_COLORS.passives, codes: ["7", "8"] },
  { code: "promoters", label: "Promoters", color: NPS_COLORS.promoters, codes: ["9", "10"] },
];

// Chart types for the KPI trend card (a simple SVG line/bar/area plot over waves).
export const KPI_TREND_CHART_TYPE_OPTIONS = [
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "area", label: "Area" },
];
